import { mdiChevronRight, mdiShieldCheck } from '@mdi/js';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';

import { Icon } from 'common/components/Icon';
import {
    ListItem,
    ListItemActionText,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import { IFileFragment } from 'graphql/types';
import { CertificateTypePreviewModal } from 'certificate/components/CertificateTypePreviewModal';
import { TControllerProps } from 'common/types';
import {
    ICertificatePreviewSource,
    TCertificatePreviewData,
} from 'certificate/types';

interface IProps {
    thumbnail?: IFileFragment | null;
    signatureFile?: IFileFragment | null;
    customTemplateFile?: IFileFragment | null;
}

export const CertificatePreviewListItem = <TFieldValues extends FieldValues>({
    thumbnail,
    signatureFile,
    customTemplateFile,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();
    const [isPreviewOpen, setPreviewOpen] = useState(false);

    const { getValues } = useFormContext<TCertificatePreviewData>();
    const name = useWatch({ name: 'name' });

    // Snapshot the current form values when the preview opens and pass them to
    // the shared modal, which owns the iframe POST plumbing. Memoising on
    // `isPreviewOpen` keeps a stable reference while open so the modal submits
    // the preview once per open instead of on every render.
    const previewCertificate = useMemo<
        ICertificatePreviewSource | undefined
    >(() => {
        if (!isPreviewOpen) return undefined;

        const {
            template,
            primaryColor,
            secondaryColor,
            title,
            subtitle,
            text,
            expiry,
        } = getValues();

        return {
            template,
            primaryColor,
            secondaryColor,
            title,
            subtitle,
            text,
            expiry,
            signatureFile,
            customTemplate: customTemplateFile,
        };
    }, [isPreviewOpen, getValues, signatureFile, customTemplateFile]);

    return (
        <>
            <ListItem button px={0} onClick={() => setPreviewOpen(true)}>
                <ListItemMedia
                    color="primary"
                    image={thumbnail?.url || ''}
                    size="small"
                >
                    <Icon path={mdiShieldCheck} size="3rem" />
                </ListItemMedia>

                <ListItemText
                    primary={
                        name ||
                        translate('certificatePreview.fallbackTitleListItem')
                    }
                />

                <ListItemActionText>{translate('preview')}</ListItemActionText>

                <ListItemSecondaryAction>
                    <Icon path={mdiChevronRight} size="3rem" />
                </ListItemSecondaryAction>
            </ListItem>

            <CertificateTypePreviewModal
                certificate={previewCertificate}
                open={isPreviewOpen}
                onClose={() => setPreviewOpen(false)}
            />
        </>
    );
};
